'use client'

import NextImage from 'next/image'
import React from 'react'

import type { Shot } from './shots'

type Props = {
  photos: Shot[]
  /** strip — лента во всю ширину, article — обложка с мелкими кадрами под ней. */
  variant: 'strip' | 'article'
}

/**
 * Фотографии с просмотром в полном размере.
 *
 * Кадры нигде не обрезаются: у каждого свои пропорции, ограничиваем только
 * высоту. Лента не пытается вместить все снимки в ширину экрана — она
 * прокручивается, поэтому вертикальные фото не превращаются в полоски.
 */
export function PhotoSet({ photos, variant }: Props) {
  const [open, setOpen] = React.useState<number | null>(null)
  const row = React.useRef<HTMLDivElement>(null)

  const close = React.useCallback(() => setOpen(null), [])
  const step = React.useCallback(
    (delta: number) => setOpen((i) => (i === null ? null : (i + delta + photos.length) % photos.length)),
    [photos.length],
  )

  // пока открыт просмотр: страница под ним не ездит, работают Esc и стрелки
  React.useEffect(() => {
    if (open === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') step(1)
      if (e.key === 'ArrowLeft') step(-1)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, close, step])

  const scrollBy = (dir: number) => {
    const el = row.current
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' })
  }

  const viewer = open !== null && photos[open] && (
    <div
      aria-modal
      className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur-sm"
      onClick={close}
      role="dialog"
    >
      <div className="flex items-center justify-between gap-4 px-4 py-3 text-sand">
        <span className="font-display text-sm tracking-wide">
          {open + 1} / {photos.length}
        </span>
        <button
          aria-label="Закрыть"
          className="rounded-full border border-sand/30 px-4 py-1.5 font-display text-sm uppercase tracking-wide hover:bg-sand/10"
          onClick={close}
          type="button"
        >
          Закрыть
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-2 pb-4">
        {/* eslint-disable-next-line @next/next/no-img-element -- просмотр открывает исходник, а не вариант под сетку */}
        <img
          alt={photos[open]!.alt}
          className="max-h-full max-w-full object-contain"
          onClick={(e) => e.stopPropagation()}
          src={photos[open]!.url}
        />
      </div>

      {photos.length > 1 && (
        <>
          <button
            aria-label="Предыдущее фото"
            className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-ink/60 px-4 py-3 font-display text-xl text-sand hover:bg-ink/80"
            onClick={(e) => {
              e.stopPropagation()
              step(-1)
            }}
            type="button"
          >
            ‹
          </button>
          <button
            aria-label="Следующее фото"
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-ink/60 px-4 py-3 font-display text-xl text-sand hover:bg-ink/80"
            onClick={(e) => {
              e.stopPropagation()
              step(1)
            }}
            type="button"
          >
            ›
          </button>
        </>
      )}
    </div>
  )

  if (variant === 'strip') {
    return (
      <>
        <div className="group relative">
          <div
            className="no-scrollbar flex h-60 snap-x gap-1 overflow-x-auto sm:h-80 lg:h-[26rem]"
            ref={row}
          >
            {photos.map((p, i) => (
              <button
                aria-label={`Открыть фото ${i + 1}`}
                className="relative h-full shrink-0 snap-start cursor-zoom-in bg-foam"
                key={p.url}
                onClick={() => setOpen(i)}
                type="button"
              >
                <NextImage
                  alt={p.alt}
                  className="h-full w-auto"
                  height={p.height}
                  sizes="(max-width: 768px) 80vw, 40vw"
                  src={p.url}
                  width={p.width}
                />
              </button>
            ))}
          </div>

          {/* листать мышью: на телефоне хватает пальца */}
          <button
            aria-label="Прокрутить влево"
            className="absolute top-1/2 left-3 hidden -translate-y-1/2 rounded-full bg-ink/55 px-3.5 py-2.5 font-display text-lg text-sand transition-opacity hover:bg-ink/80 sm:block"
            onClick={() => scrollBy(-1)}
            type="button"
          >
            ‹
          </button>
          <button
            aria-label="Прокрутить вправо"
            className="absolute top-1/2 right-3 hidden -translate-y-1/2 rounded-full bg-ink/55 px-3.5 py-2.5 font-display text-lg text-sand transition-opacity hover:bg-ink/80 sm:block"
            onClick={() => scrollBy(1)}
            type="button"
          >
            ›
          </button>
        </div>
        {viewer}
      </>
    )
  }

  const [cover, ...rest] = photos
  if (!cover) return null

  return (
    <>
      <div className="flex flex-col gap-2">
        <button
          aria-label="Открыть фото"
          className="block cursor-zoom-in self-start"
          onClick={() => setOpen(0)}
          type="button"
        >
          <NextImage
            alt={cover.alt}
            className="h-auto max-h-[26rem] w-auto max-w-full rounded-2xl bg-foam object-contain"
            height={cover.height}
            sizes="(max-width: 768px) 100vw, 40vw"
            src={cover.url}
            width={cover.width}
          />
        </button>

        {rest.length > 0 && (
          <div className="no-scrollbar flex h-20 gap-2 overflow-x-auto">
            {rest.map((p, i) => (
              <button
                aria-label={`Открыть фото ${i + 2}`}
                className="h-full shrink-0 cursor-zoom-in"
                key={p.url}
                onClick={() => setOpen(i + 1)}
                type="button"
              >
                <NextImage
                  alt={p.alt}
                  className="h-full w-auto rounded-lg bg-foam"
                  height={p.height}
                  sizes="120px"
                  src={p.url}
                  width={p.width}
                />
              </button>
            ))}
          </div>
        )}
      </div>
      {viewer}
    </>
  )
}
